/**
 * The Constraints card on the Solution drawer's Pod & Fit tab — what the Pod's
 * Primaries are scored against (Fit Score › Constraint Match, src/api/fit-score.ts).
 *
 * One card, one row per Constraint group, the same anatomy as the Account page's
 * Constraint Profile card so the two read as the same thing: hue dot + group label on
 * the left, chips on the right, and a dashed "+ Add" per row that opens the shared
 * picker (ConstraintPickerPopover) scrolled to that group.
 *
 * Chips always render from the Solution Detail payload, never from the library: a
 * Constraint retired in Configuration stays on the Solution and keeps scoring (neither
 * getSolutionDetail nor the Fit engine filters on `active`), so it must stay visible —
 * and removable. Such a chip is marked, once the library has loaded and can vouch that
 * the id is absent.
 *
 * Editing needs `editSolutionConstraints` — ADMIN and RM, the same capability the two API
 * routes check (src/index.ts); every other role gets the read-only card.
 */

import { Building2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Tag } from "@/components/ui/tag";
import { useConstraintLibrary } from "@/hooks/use-accounts";
import {
  type SolutionConstraintItem,
  useAddSolutionConstraint,
  useRemoveSolutionConstraint,
} from "@/hooks/use-solution-constraints";
import { useSession } from "@/lib/auth-client";
import { constraintGroupHue } from "@/lib/constraint-colors";
import {
  bucketByGroup,
  type ConstraintPickerOption,
  constraintSummaryText,
  summarizeConstraints,
  toPickerOptions,
} from "@/lib/constraint-groups";
import { hasCapability } from "@/lib/permissions";
import { ConstraintPickerPopover } from "./ConstraintPickerPopover";

/** The slice of the Solution Detail payload this card reads. */
interface SolutionConstraintsSource {
  id: string;
  accountName: string;
  constraints: SolutionConstraintItem[];
}

export function SolutionConstraintsEditor({ solution }: { solution: SolutionConstraintsSource }) {
  const { data: session } = useSession();
  const canEdit = hasCapability(session?.user?.role, "editSolutionConstraints");

  const library = useConstraintLibrary();
  const add = useAddSolutionConstraint(solution.id);
  const remove = useRemoveSolutionConstraint(solution.id);

  // Ids with a request in flight. Local state rather than the mutation's own
  // `isPending`: that reflects only its latest call, and the picker lets several run.
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(() => new Set());

  const options = useMemo(() => toPickerOptions(library.data ?? []), [library.data]);
  const libraryById = useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options],
  );
  const buckets = useMemo(() => bucketByGroup(solution.constraints), [solution.constraints]);
  const selectedIds = useMemo(
    () => new Set(solution.constraints.map((c) => c.id)),
    [solution.constraints],
  );
  const inheritedIds = useMemo(
    () => new Set(solution.constraints.filter((c) => c.inherited).map((c) => c.id)),
    [solution.constraints],
  );
  const summary = summarizeConstraints(solution.constraints);

  function change(item: { id: string; name: string; inherited: boolean }, next: boolean) {
    if (pendingIds.has(item.id)) return;
    setPendingIds((prev) => new Set(prev).add(item.id));
    const variables = {
      constraintId: item.id,
      name: item.name,
      inherited: item.inherited,
      accountName: solution.accountName,
    };
    // Only the local busy flag lives on this call; invalidation and toasts are on the
    // hooks, so they still run if the drawer closes before the reply.
    const settle = {
      onSettled: () =>
        setPendingIds((prev) => {
          const rest = new Set(prev);
          rest.delete(item.id);
          return rest;
        }),
    };
    if (next) add.mutate(variables, settle);
    else remove.mutate(variables, settle);
  }

  function onPickerToggle(option: ConstraintPickerOption, next: boolean) {
    change(
      { id: option.value, name: option.label, inherited: inheritedIds.has(option.value) },
      next,
    );
  }

  return (
    <div>
      <p className="text-sm text-foreground">
        {constraintSummaryText(summary, solution.accountName)}
      </p>
      <p className="mb-2 text-xs text-muted-foreground">
        Primaries are scored against these. Changes recalculate the Fit Score.
      </p>

      <div className="rounded-md border border-border bg-card px-3">
        {buckets.map(({ group, items }) => (
          <div
            key={group}
            className="grid grid-cols-[120px_1fr] items-start gap-3 border-t border-border py-2.5 first:border-t-0"
          >
            <div className="flex items-center gap-2 pt-1">
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ background: constraintGroupHue(group) }}
                aria-hidden="true"
              />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {group}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
              {items.length === 0 && <span className="text-sm text-muted-foreground">None</span>}
              {items.map((c) => {
                const inLibrary = libraryById.get(c.id);
                const missing = library.isSuccess && inLibrary === undefined;
                const origin = missing
                  ? "No longer in the active Constraint library"
                  : c.inherited
                    ? `Inherited from ${solution.accountName}'s constraint profile`
                    : `Solution-specific — not inherited from ${solution.accountName}'s constraint profile`;
                const description = inLibrary?.description;
                return (
                  <Tag
                    key={c.id}
                    variant={c.inherited || missing ? "gray" : "stroke"}
                    title={description ? `${origin} — ${description}` : origin}
                    icon={
                      c.inherited ? (
                        <Building2 className="size-3 text-muted-foreground" aria-hidden="true" />
                      ) : undefined
                    }
                    disabled={pendingIds.has(c.id)}
                    onDismiss={canEdit ? () => change(c, false) : undefined}
                    dismissLabel={`Remove ${c.name}`}
                  >
                    {c.name}
                  </Tag>
                );
              })}
              {canEdit && (
                <ConstraintPickerPopover
                  options={options}
                  initialGroup={group}
                  selectedIds={selectedIds}
                  inheritedIds={inheritedIds}
                  pendingIds={pendingIds}
                  onToggle={onPickerToggle}
                  isLoading={library.isLoading}
                  isError={library.isError}
                  onRetry={() => library.refetch()}
                  triggerLabel={`Add ${group} constraint`}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
